import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { getExternalTermsUrl } from 'wtb-utils';
import { Typography } from 'wtb-components';
import {
  Accordion,
  Checkbox,
  Dialog,
  Link,
  Skeleton,
  TermsConditions
} from 'wtb-components';
import { useLocaleContext, useTransactionContext, useAppContext } from 'context';
import { useDeviceInfo } from 'hooks';
import styles from './Terms.module.css';

const AccordionTitle = () => {
  const { translate } = useLocaleContext();
  const { isLoading } = useAppContext();

  if (isLoading) {
    return <Skeleton width={150} aria-label="skeleton" />;
  } else {
    return (
      <Typography
        className={styles.termsTitle}
        variant="subtitle1"
        component="span"
      >
        {translate('terms.checkbox.title')}
      </Typography>
    );
  }
};

const CheckboxLabel = ({ type, terms = [], openDialog }) => {
  const { merchant: { name } } = useTransactionContext();
  const { locale, translate } = useLocaleContext();

  const termExists = terms && terms.length && terms[0]?.content[locale];

  if (!termExists) return null;

  return (
    <Link
      aria-label={`${type}-label`}
      id={`${type}-terms-link`}
      className={styles.link}
      variant="body2"
      component="div"
      role="button"
      color="tertiary"
      underline="hover"
      tabIndex={0}
      onKeyDown={openDialog(terms)}
      onClick={openDialog(terms, type)}
    >
      {type === 'wtb'
        ? translate('terms.wtb.readMore')
        : translate('terms.merchant.readMore', { merchant: name })}
    </Link>
  );
};

const Terms = ({ tabIndex }) => {
  const history = useHistory();
  const location = useLocation();

  const { locale, translate } = useLocaleContext();
  const { isLoading } = useAppContext();
  const {
    merchant,
    wtbTerms,
    areTermsAccepted,
    setAreTermsAccepted
  } = useTransactionContext();

  const deviceInfo = useDeviceInfo();
  const [ dialogData, setDialogData ] = useState([]);
  const [ isDialogOpen, setIsDialogOpen ] = useState(false);
  const [ termsType, setTermsType ] = useState('');

  const openDialog = (terms, type) => (event) => {
    if (event.type === 'keydown' && event.keyCode !== 13) return;

    // If terms are a single URL, open in a new tab instead of the dialog
    const externalUrl = getExternalTermsUrl(terms, locale);

    if (externalUrl) {
      window.open(externalUrl, '_blank', 'noreferrer');
      return;
    }

    setTermsType(type);
    setDialogData(terms);
    history.push(`${location.pathname}#terms`);
  };

  const closeDialog = () => { history.push(location.pathname); };

  useEffect(() => {
    if (location?.hash === '#terms') {
      setIsDialogOpen(true);
    } else {
      setIsDialogOpen(false);
    }
  }, [ location.hash ]);

  const isMobile = deviceInfo?.device?.type === 'mobile';

  const handleTermsChange = (event) => {
    if (event.type === 'keyup') {
      setAreTermsAccepted(true);
    } else {
      setAreTermsAccepted(!areTermsAccepted);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.wrapper} aria-label="terms">
        <Checkbox
          size="small"
          id="terms-checkbox"
          className={styles.checkbox}
          wrapperClassName={styles.checkboxWrapper}
          color="primary"
          checked={areTermsAccepted}
          tabIndex={tabIndex}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();

            handleTermsChange(event);
          }}
        />

        <Accordion
          aria-label="terms-accordion"
          id="terms-accordion"
          className={styles.termsAccordion}
          elevation={0}
          icon
          BodyProps={{ className: styles.termsBody }}
          HeaderProps={{ className: styles.termsHeader }}
          header={<AccordionTitle />}
        >
          <div className={styles.accordionBody}>
            <CheckboxLabel type="merchant" terms={merchant.terms} openDialog={openDialog} />
            <CheckboxLabel type="wtb" aria-label="wtb-label" terms={wtbTerms} openDialog={openDialog} />
          </div>
        </Accordion>
      </div>

      {dialogData.length > 0 && (
        <Dialog
          id={`terms-${termsType}`}
          popupClassName={styles.dialog}
          fullScreen={isMobile}
          title={translate('terms.dialog.title')}
          titleClassName={styles.dialogHeader}
          TitleProps={{ Typography: { variant: 'h6' }}}
          closeButtonClassName={styles.dialogClose}
          closeOnBackdropClick
          CloseProps={{ id: 'terms-close-button' }}
          onClose={closeDialog}
          isOpen={isDialogOpen}
        >
          <div id="dialog-content" className={styles.dialogContent}>
            <TermsConditions
              sections={dialogData}
              isLoading={isLoading}
              locale={locale}
              contentId="dialog-content"
            />
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default Terms;
